import { ownsPurchaser } from '@buyerflo/core/schemas/purchaser'
import {
    Active,
    DndContext,
    DragOverlay,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PurchaserFilesApiData, usePurchaserFilesApi, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import axios from 'axios'
import Loading from 'components/Loading'
import Button from 'components/button'
import FileUploadButton from 'components/fileUploadButton'
import {
    AddRounded,
    Check,
    ChevronRightRounded,
    CloseRounded,
    ContentCopy,
    DeleteRounded,
    DownloadRounded,
    EmailRounded,
    FileUploadRoundedIcon,
    Folder,
    InsertDriveFileRounded,
    KeyboardArrowDownRounded,
    Search,
    Visibility,
} from 'components/icons'
import PdfModal from 'components/pdfModal'
import Skeleton from 'components/skeleton'
import { useShowSnack } from 'components/snackbar'
import copy from 'copy-to-clipboard'
import { format } from 'date-fns'
import { pick, prop } from 'ramda'
import { useMemo, useRef, useState } from 'react'
import { Dialog, Input, SizableText, Spinner, Text, View, XStack, YStack } from 'tamagui'
import { useTRPC, useTRPCClient } from 'utils/trpc'
import { CLIENT_DETAIL_MAX_WIDTH } from '../helpers'
import { usePurchaserContext } from '../purchaserProvider'
import EmailModal from './emailModal'
import {
    childFiles,
    childFolders,
    countFilesUnder,
    extColor,
    formatBytes,
    getExt,
    isPreviewable,
    lastSegment,
    parentOf,
} from './helpers'

const { VITE_APP_STAGE } = import.meta.env

const RADIUS = { sm: 6, md: 10, lg: 14 }
/** Destination encoded into droppable ids; the folder path is read back after "::". */
const dest = (ns: string, folderName: string) => `${ns}::${folderName}`
const readDest = (id: string) => id.split('::')[1] ?? ''

const formatDate = (iso?: string) => (iso ? format(new Date(iso), 'dd MMM yyyy') : '')

type Preview = { name: string; url: string }

interface RowCallbacks {
    onOpen: (file: PurchaserFilesApiData) => Promise<void> | void
    onDownload: (file: PurchaserFilesApiData) => Promise<void> | void
    onDelete: (file: PurchaserFilesApiData) => void
}

// ─── File row ──────────────────────────────────────────────────────────────
const FileRow = ({
    file,
    location,
    canDelete,
    opening,
    onOpen,
    onDownload,
    onDelete,
}: {
    file: PurchaserFilesApiData
    location?: string[]
    canDelete: boolean
    opening?: boolean
} & RowCallbacks) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: file.Key })
    const color = extColor(file.name)
    const ext = getExt(file.name) || 'FILE'
    const Icon = file.isEmail ? EmailRounded : InsertDriveFileRounded
    const name = file.name.split('/').pop()!.replaceAll('%2F', '/')

    const actionBtn = (props: any) => (
        <Button
            size="$2"
            circular
            chromeless
            width={28}
            height={28}
            padding={0}
            backgroundColor="transparent"
            hoverStyle={{ backgroundColor: '$backgroundHover' }}
            {...props}
        />
    )

    return (
        <XStack
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            className="bf-file-row"
            alignItems="center"
            gap="$3"
            paddingVertical="$2"
            paddingHorizontal="$2.5"
            borderRadius={RADIUS.sm}
            borderWidth={1}
            borderColor="transparent"
            hoverStyle={{ backgroundColor: '$backgroundHover' }}
            cursor="pointer"
            opacity={isDragging ? 0.5 : 1}
            onPress={() => onOpen(file)}
        >
            <View
                width={32}
                height={32}
                borderRadius={7}
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                backgroundColor={color + '22'}
            >
                {opening ? (
                    <Spinner
                        size="small"
                        color={color as any}
                    />
                ) : (
                    <Icon
                        size={16}
                        color={color as any}
                    />
                )}
            </View>

            <YStack
                flex={1}
                minWidth={0}
            >
                <SizableText
                    size="$2"
                    lineHeight={18}
                    fontWeight="500"
                    color="$color"
                    numberOfLines={1}
                >
                    {name}
                </SizableText>
                {location && location.length > 0 && (
                    <XStack
                        alignItems="center"
                        gap="$1"
                        marginTop={1}
                    >
                        <Folder
                            size={10}
                            color="$colorMuted"
                        />
                        <SizableText
                            fontSize={11}
                            lineHeight={14}
                            color="$colorMuted"
                            numberOfLines={1}
                        >
                            {location.join(' / ')}
                        </SizableText>
                    </XStack>
                )}
            </YStack>

            <View
                paddingVertical={1}
                paddingHorizontal={6}
                borderRadius={3}
                flexShrink={0}
                backgroundColor={color as any}
            >
                <SizableText
                    style={{ fontSize: 10 }}
                    fontWeight="700"
                    fontFamily="$mono"
                    color="white"
                >
                    {ext}
                </SizableText>
            </View>

            <SizableText
                width={70}
                flexShrink={0}
                textAlign="right"
                size="$1"
                color="$colorSoft"
                fontFamily="$mono"
                display="none"
                $md={{ display: 'flex' }}
            >
                {formatBytes(file.Size)}
            </SizableText>

            <SizableText
                width={95}
                flexShrink={0}
                textAlign="right"
                style={{ fontSize: 11 }}
                color="$colorMuted"
                fontFamily="$mono"
                display="none"
                $md={{ display: 'flex' }}
            >
                {formatDate(file.LastModified)}
            </SizableText>

            <XStack
                className="bf-file-actions"
                gap="$0.5"
                flexShrink={0}
                justifyContent="flex-end"
                alignItems="center"
            >
                {actionBtn({
                    icon: <Visibility size={15} />,
                    onPress: (e: any) => {
                        e.stopPropagation()
                        return onOpen(file)
                    },
                })}
                {actionBtn({
                    icon: <DownloadRounded size={15} />,
                    onPress: (e: any) => {
                        e.stopPropagation()
                        return onDownload(file)
                    },
                })}
                {canDelete &&
                    actionBtn({
                        icon: <DeleteRounded size={15} />,
                        onPress: (e: any) => {
                            e.stopPropagation()
                            onDelete(file)
                        },
                    })}
            </XStack>
        </XStack>
    )
}

// ─── Skeleton row (shown per file while an upload is in flight) ───────────────
const FileSkeletonRow = () => (
    <XStack
        alignItems="center"
        gap="$3"
        paddingVertical="$2"
        paddingHorizontal="$2.5"
        borderRadius={RADIUS.sm}
    >
        <Skeleton
            width={32}
            height={32}
            borderRadius={7}
            flexShrink={0}
        />
        <Skeleton
            flex={1}
            maxWidth={200}
            height={12}
            borderRadius={4}
        />
        <Spinner
            size="small"
            color="$colorMuted"
        />
    </XStack>
)

// ─── Folder card (main pane) ─────────────────────────────────────────────────
const FolderCard = ({
    folder,
    files,
    onOpen,
    dragDisabled,
}: {
    folder: PurchaserFilesApiData
    files: PurchaserFilesApiData[]
    onOpen: (folderName: string) => void
    dragDisabled: boolean
}) => {
    const drag = useDraggable({ id: folder.Key, disabled: dragDisabled })
    const drop = useDroppable({ id: dest('card', folder.name) })
    const setRefs = (node: HTMLElement | null) => {
        drag.setNodeRef(node)
        drop.setNodeRef(node)
    }
    const fileCount = countFilesUnder(files, folder.name)
    const subCount = childFolders(files, folder.name).length

    return (
        <XStack
            ref={setRefs as any}
            {...drag.attributes}
            {...drag.listeners}
            alignItems="center"
            gap="$3"
            paddingVertical="$3"
            paddingHorizontal="$3.5"
            borderWidth={1}
            borderColor={drop.isOver ? '$borderColorStrong' : '$borderColor'}
            borderRadius={RADIUS.md}
            backgroundColor={drop.isOver ? '$backgroundRaised' : '$backgroundStrong'}
            cursor="pointer"
            opacity={drag.isDragging ? 0.5 : 1}
            hoverStyle={{ borderColor: '$borderColorStrong', backgroundColor: '$backgroundRaised' }}
            animation="quick"
            onPress={() => onOpen(folder.name)}
        >
            <View
                width={36}
                height={36}
                borderRadius={8}
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                backgroundColor="$accentSoft"
            >
                <Folder
                    size={20}
                    color="$accentText"
                />
            </View>
            <YStack
                flex={1}
                minWidth={0}
            >
                <SizableText
                    size="$2"
                    lineHeight={18}
                    fontWeight="500"
                    color="$color"
                    numberOfLines={1}
                    marginBottom={2}
                >
                    {lastSegment(folder.name)}
                </SizableText>
                <SizableText
                    fontSize={11}
                    lineHeight={15}
                    color="$colorMuted"
                >
                    {fileCount} {fileCount === 1 ? 'file' : 'files'} · {subCount}{' '}
                    {subCount === 1 ? 'subfolder' : 'subfolders'}
                </SizableText>
            </YStack>
            <ChevronRightRounded
                size={14}
                color="$colorMuted"
            />
        </XStack>
    )
}

// ─── Tree node (sidebar) ─────────────────────────────────────────────────────
const TreeNode = ({
    folderName,
    depth,
    files,
    currentPrefix,
    expanded,
    onToggle,
    onNavigate,
}: {
    folderName: string
    depth: number
    files: PurchaserFilesApiData[]
    currentPrefix: string
    expanded: Set<string>
    onToggle: (folderName: string) => void
    onNavigate: (folderName: string) => void
}) => {
    const isRoot = folderName === ''
    const isCurrent = folderName === currentPrefix
    const isOpen = isRoot || expanded.has(folderName)
    const children = childFolders(files, folderName)
    const drop = useDroppable({ id: dest('tree', folderName) })

    return (
        <>
            <XStack
                ref={drop.setNodeRef as any}
                alignItems="center"
                gap="$1.5"
                minHeight={28}
                paddingVertical="$1.5"
                paddingRight="$2"
                paddingLeft={8 + depth * 16}
                borderRadius={RADIUS.sm}
                cursor="pointer"
                userSelect="none"
                backgroundColor={isCurrent ? '$accentSoft' : drop.isOver ? '$backgroundHover' : 'transparent'}
                hoverStyle={{ backgroundColor: isCurrent ? '$accentSoft' : '$backgroundHover' }}
                onPress={() => onNavigate(folderName)}
            >
                <View
                    width={16}
                    height={16}
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    onPress={(e: any) => {
                        e.stopPropagation()
                        if (children.length) onToggle(folderName)
                    }}
                >
                    {children.length > 0 && (
                        <KeyboardArrowDownRounded
                            size={12}
                            color="$colorMuted"
                            style={{
                                transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                transition: 'transform 0.12s',
                            }}
                        />
                    )}
                </View>
                <Folder
                    size={13}
                    color={isCurrent ? '$accentText' : '$colorMuted'}
                />
                <SizableText
                    flex={1}
                    minWidth={0}
                    numberOfLines={1}
                    size="$2"
                    fontWeight={isCurrent ? '500' : '400'}
                    color={isCurrent ? '$accentText' : '$colorSoft'}
                >
                    {isRoot ? 'Files' : lastSegment(folderName)}
                </SizableText>
                <View
                    paddingVertical={1}
                    paddingHorizontal={5}
                    borderRadius={4}
                    backgroundColor={isCurrent ? '$accentSoft' : '$backgroundRaised'}
                >
                    <SizableText
                        fontFamily="$mono"
                        color={isCurrent ? '$accentText' : '$colorFaint'}
                        style={{ fontSize: 10, lineHeight: 14 }}
                    >
                        {countFilesUnder(files, folderName)}
                    </SizableText>
                </View>
            </XStack>
            {isOpen &&
                children.map(child => (
                    <TreeNode
                        key={child.Key}
                        folderName={child.name}
                        depth={depth + 1}
                        files={files}
                        currentPrefix={currentPrefix}
                        expanded={expanded}
                        onToggle={onToggle}
                        onNavigate={onNavigate}
                    />
                ))}
        </>
    )
}

// ─── Breadcrumb segment (droppable) ──────────────────────────────────────────
const Crumb = ({
    folderName,
    label,
    current,
    onNavigate,
}: {
    folderName: string
    label: string
    current: boolean
    onNavigate: (folderName: string) => void
}) => {
    const drop = useDroppable({ id: dest('crumb', folderName) })
    return (
        <View
            ref={drop.setNodeRef as any}
            paddingVertical={3}
            paddingHorizontal={7}
            borderRadius={5}
            cursor="pointer"
            backgroundColor={drop.isOver ? '$backgroundHover' : 'transparent'}
            hoverStyle={{ backgroundColor: '$backgroundHover' }}
            onPress={() => onNavigate(folderName)}
        >
            <SizableText
                size="$2"
                fontWeight="500"
                color={current ? '$color' : '$colorMuted'}
            >
                {label}
            </SizableText>
        </View>
    )
}

const sectionHeaderStyle = {
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase' as const,
    color: '$colorMuted',
    fontWeight: '600' as const,
}

const Files = () => {
    const trpc = useTRPC()
    const trpcClient = useTRPCClient()
    const { purchaser } = usePurchaserContext()
    const showSnack = useShowSnack()
    const filesApi = usePurchaserFilesApi()
    const usersApi = useUsersApi()
    const { user } = useUserData()
    const queryClient = useQueryClient()

    const filesQueryKey = trpc.purchaser.getFiles.queryKey({ purchaserId: purchaser.purchaserId })
    const getFileUploadUrlsMutation = useMutation(trpc.purchaser.getFileUploadUrls.mutationOptions())
    const moveFileMutation = useMutation(trpc.purchaser.moveFile.mutationOptions())
    const deleteFileMutation = useMutation(trpc.purchaser.deleteFile.mutationOptions())

    const [path, setPath] = useState<string[]>([])
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    const [query, setQuery] = useState('')
    const [copied, setCopied] = useState(false)
    const [newFolder, setNewFolder] = useState<string | null>(null)
    const [creatingFolder, setCreatingFolder] = useState(false)
    // Guards against a double commit/cancel (e.g. pressing Enter then clicking the confirm button).
    const folderActionTaken = useRef(false)
    const [preview, setPreview] = useState<Preview | undefined>()
    const [deleteTarget, setDeleteTarget] = useState<PurchaserFilesApiData | undefined>()
    const [active, setActive] = useState<Active | null>(null)
    const [uploadingFiles, setUploadingFiles] = useState<File[]>([])
    const [openingKey, setOpeningKey] = useState<string | null>(null)

    const files = filesApi.data
    const activeItem = useMemo(
        () => (active ? (files ?? []).find(f => f.Key === active.id) : undefined),
        [active, files],
    )

    const owns = ownsPurchaser(purchaser)
    const formEnabled =
        user.permissions.includes('purchasers.fullControl') ||
        (user.permissions.includes('purchasers.edit') &&
            (owns(user.userId) ||
                (user.salesAssistantLeadAgentId && owns(user.salesAssistantLeadAgentId)) ||
                (user.salesAssistantConjunctionalAgencyId &&
                    usersApi.data?.find(x => owns(x.userId))?.conjunctionalAgencyId ==
                        user.salesAssistantConjunctionalAgencyId)))
    const formDisabled = !formEnabled
    const canDelete = user.permissions.includes('purchasers.fullControl')

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

    const fileEmail = `${purchaser.storageName}@storage${VITE_APP_STAGE == 'prod' ? '' : '.' + VITE_APP_STAGE}.buyerflo.com.au`

    const copyEmail = () => {
        copy(fileEmail)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    // ── data mutations ──────────────────────────────────────────────────────
    const uploadFiles = async (toUpload: File[], folderSegments?: string[]) => {
        const folder = (folderSegments ?? path).filter(x => x != '')
        // Folder-creation placeholders get their own inline row; only show upload skeletons for real files.
        const realFiles = toUpload.filter(f => f.type !== 'application/x-directory')
        setUploadingFiles(prev => [...prev, ...realFiles])
        try {
            const { presignedUrls } = await getFileUploadUrlsMutation.mutateAsync({
                purchaserId: purchaser.purchaserId,
                folder,
                files: toUpload.map(pick(['name', 'type'])),
            })
            await Promise.all(
                toUpload.map(file =>
                    axios.put(presignedUrls[file.name], file, {
                        headers: { 'Content-Type': file.type },
                    }),
                ),
            )
            showSnack('File(s) uploaded', 'success')
        } catch (e) {
            showSnack('Error uploading file(s)', 'danger')
        } finally {
            setUploadingFiles(prev => prev.filter(f => !realFiles.includes(f)))
            await queryClient.invalidateQueries({ queryKey: filesQueryKey })
        }
    }

    const moveFiles = async (source: PurchaserFilesApiData, destinationFolder: string) => {
        const all = files!
        const sourceParentFolder = parentOf(source.name)
        const sources = source.isFolder
            ? all.filter(f => f.Key.startsWith(source.name)).map(prop('Key'))
            : [source.Key]

        const rename = (key: string) => {
            const from = sourceParentFolder ? sourceParentFolder + '/' : ''
            const to = destinationFolder == '' ? '' : destinationFolder + '/'
            // Only rewrite the leading folder segment. An unanchored String.replace
            // can match (and corrupt) an occurrence of the folder name later in the key.
            if (!from) return to + key
            return key.startsWith(from) ? to + key.slice(from.length) : key
        }

        try {
            const results = (await moveFileMutation.mutateAsync({
                purchaserId: purchaser.purchaserId,
                moves: sources.map(src => ({ source: src, destination: rename(src) })),
            })) as { success: boolean }[]
            if (results.some(x => !x.success)) showSnack('Error moving files', 'danger')
        } catch (e) {
            showSnack('Error moving files', 'danger')
        } finally {
            await queryClient.invalidateQueries({ queryKey: filesQueryKey })
        }
    }

    const createFolder = async (name: string) => {
        const folderFile = new File([], name + '/', { type: 'application/x-directory' })
        await uploadFiles([folderFile], path)
    }

    // Escape or the ✗ button dismisses the row — no folder is created.
    const cancelNewFolder = () => {
        if (folderActionTaken.current) return
        folderActionTaken.current = true
        setNewFolder(null)
    }

    // Enter confirms: validate, then hold the row in a loading state until the
    // folder is actually created before clearing it.
    const commitNewFolder = async () => {
        if (folderActionTaken.current) return
        const trimmed = (newFolder ?? '').trim()
        if (!trimmed) {
            cancelNewFolder()
            return
        }
        if (childFolders(files ?? [], path.join('/')).some(f => lastSegment(f.name) === trimmed)) {
            // Keep the input open so the clashing name can be corrected.
            showSnack('A folder with that name already exists', 'danger')
            return
        }
        folderActionTaken.current = true
        setCreatingFolder(true)
        await createFolder(trimmed)
        setCreatingFolder(false)
        setNewFolder(null)
    }

    const getUrl = (file: PurchaserFilesApiData) =>
        trpcClient.purchaser.getFile.query({
            purchaserId: purchaser.purchaserId,
            filename: file.Key,
        })

    // Open a file: preview PDFs/images/emails in-app, open everything else in a new tab.
    const handleOpenFile = async (file: PurchaserFilesApiData) => {
        if (openingKey === file.Key) return
        setOpeningKey(file.Key)
        try {
            const url = await getUrl(file)
            if (isPreviewable(file.name)) {
                setPreview({ name: file.Key.split('/').pop()!, url })
            } else {
                window.open(url, '_blank')
            }
        } catch (e) {
            showSnack('Could not open file', 'danger')
        } finally {
            setOpeningKey(null)
        }
    }

    const handleDownload = async (file: PurchaserFilesApiData) => {
        try {
            const url = await getUrl(file)
            window.open(url, '_blank')
        } catch (e) {
            showSnack('Could not download file', 'danger')
        }
    }

    const confirmDelete = async () => {
        if (!deleteTarget) return
        try {
            await deleteFileMutation.mutateAsync({
                purchaserId: purchaser.purchaserId,
                filename: deleteTarget.Key,
            })
            showSnack('File deleted', 'success')
        } catch (e) {
            showSnack('Failed to delete file', 'danger')
        } finally {
            setDeleteTarget(undefined)
            await queryClient.invalidateQueries({ queryKey: filesQueryKey })
        }
    }

    // ── navigation ──────────────────────────────────────────────────────────
    const navigateTo = (folderName: string) => {
        setQuery('')
        setNewFolder(null)
        setPath(folderName === '' ? [] : folderName.split('/'))
        // Reveal the target's ancestry in the tree
        if (folderName) {
            setExpanded(prev => {
                const next = new Set(prev)
                const segs = folderName.split('/')
                for (let i = 1; i <= segs.length; i++) next.add(segs.slice(0, i).join('/'))
                return next
            })
        }
    }

    const toggleExpand = (folderName: string) =>
        setExpanded(prev => {
            const next = new Set(prev)
            if (next.has(folderName)) next.delete(folderName)
            else next.add(folderName)
            return next
        })

    const startNewFolder = () => {
        folderActionTaken.current = false
        setQuery('')
        setNewFolder('New folder')
    }

    const onDragEnd = ({ active: a, over }: { active: Active; over: any }) => {
        setActive(null)
        if (!over || !files) return
        const item = files.find(f => f.Key === a.id)
        if (!item) return
        const destination = readDest(String(over.id))
        const sourceParent = parentOf(item.name)
        const droppingIntoSelf =
            item.isFolder && (destination === item.name || destination.startsWith(item.name + '/'))
        if (destination !== sourceParent && !droppingIntoSelf) moveFiles(item, destination)
    }

    if (!files) return <Loading />

    const currentPrefix = path.join('/')
    const currentFolders = childFolders(files, currentPrefix)
    const currentFiles = childFiles(files, currentPrefix)

    const searchHits = query
        ? files
              .filter(f => !f.isFolder && f.name.toLowerCase().includes(query.toLowerCase()))
              .map(f => ({ file: f, location: parentOf(f.name).split('/').filter(Boolean) }))
        : null

    const rowCallbacks: RowCallbacks = {
        onOpen: handleOpenFile,
        onDownload: handleDownload,
        onDelete: setDeleteTarget,
    }

    return (
        <YStack maxWidth={CLIENT_DETAIL_MAX_WIDTH}>
            {/* Forward-email banner */}
            {!formDisabled && (
                <XStack
                    alignItems="center"
                    gap="$2.5"
                    paddingVertical="$2.5"
                    paddingHorizontal="$3.5"
                    marginBottom="$3.5"
                    backgroundColor="$backgroundStrong"
                    borderWidth={1}
                    borderColor="$borderColor"
                    borderRadius={RADIUS.md}
                >
                    <XStack
                        flex={1}
                        minWidth={0}
                        alignItems="center"
                        gap="$2"
                    >
                        <EmailRounded
                            size={14}
                            color="$colorMuted"
                        />
                        <SizableText
                            size="$1"
                            color="$colorMuted"
                            flexShrink={0}
                        >
                            Attach emails by forwarding to
                        </SizableText>
                        <SizableText
                            size="$1"
                            fontFamily="$mono"
                            color="$color"
                            numberOfLines={1}
                        >
                            {fileEmail}
                        </SizableText>
                    </XStack>
                    <Button
                        size="$2"
                        chromeless
                        height={28}
                        paddingHorizontal="$2.5"
                        backgroundColor="transparent"
                        hoverStyle={{ backgroundColor: '$backgroundHover' }}
                        icon={copied ? <Check size={13} /> : <ContentCopy size={13} />}
                        onPress={copyEmail}
                    >
                        <SizableText size="$1">{copied ? 'Copied' : 'Copy'}</SizableText>
                    </Button>
                </XStack>
            )}

            <DndContext
                sensors={sensors}
                onDragStart={({ active: a }) => setActive(a)}
                onDragEnd={onDragEnd}
                onDragCancel={() => setActive(null)}
            >
                <XStack
                    borderWidth={1}
                    borderColor="$borderColor"
                    borderRadius={RADIUS.lg}
                    backgroundColor="$backgroundStrong"
                    overflow="hidden"
                    minHeight={520}
                >
                    {/* Tree sidebar (desktop) */}
                    <YStack
                        width={260}
                        flexShrink={0}
                        borderRightWidth={1}
                        borderRightColor="$borderColor"
                        display="none"
                        $md={{ display: 'flex' }}
                    >
                        <XStack
                            alignItems="center"
                            justifyContent="space-between"
                            paddingVertical="$3"
                            paddingHorizontal="$3.5"
                            borderBottomWidth={1}
                            borderBottomColor="$borderColor"
                        >
                            <SizableText {...sectionHeaderStyle}>Folders</SizableText>
                            {!formDisabled && (
                                <Button
                                    size="$2"
                                    circular
                                    chromeless
                                    width={26}
                                    height={26}
                                    padding={0}
                                    backgroundColor="transparent"
                                    hoverStyle={{ backgroundColor: '$backgroundHover' }}
                                    icon={<AddRounded size={14} />}
                                    onPress={startNewFolder}
                                />
                            )}
                        </XStack>
                        <YStack
                            flex={1}
                            paddingVertical="$1.5"
                            paddingHorizontal="$1"
                            overflow="auto"
                            className="hide-scrollbar"
                        >
                            <TreeNode
                                folderName=""
                                depth={0}
                                files={files}
                                currentPrefix={currentPrefix}
                                expanded={expanded}
                                onToggle={toggleExpand}
                                onNavigate={navigateTo}
                            />
                        </YStack>
                    </YStack>

                    {/* Main pane */}
                    <YStack
                        flex={1}
                        minWidth={0}
                    >
                        {/* Header: breadcrumbs + actions */}
                        <XStack
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            gap="$3"
                            paddingVertical="$3"
                            paddingHorizontal="$4"
                            borderBottomWidth={1}
                            borderBottomColor="$borderColor"
                        >
                            <XStack
                                alignItems="center"
                                flexWrap="wrap"
                                gap="$1"
                            >
                                <Crumb
                                    folderName=""
                                    label="Files"
                                    current={path.length === 0}
                                    onNavigate={navigateTo}
                                />
                                {path.map((seg, i) => (
                                    <XStack
                                        key={i}
                                        alignItems="center"
                                        gap="$1"
                                    >
                                        <ChevronRightRounded
                                            size={11}
                                            color="$colorFaint"
                                        />
                                        <Crumb
                                            folderName={path.slice(0, i + 1).join('/')}
                                            label={seg}
                                            current={i === path.length - 1}
                                            onNavigate={navigateTo}
                                        />
                                    </XStack>
                                ))}
                            </XStack>

                            <XStack
                                alignItems="center"
                                gap="$2"
                            >
                                <XStack
                                    alignItems="center"
                                    gap="$1.5"
                                    width={200}
                                    height={32}
                                    paddingHorizontal="$2.5"
                                    borderWidth={1}
                                    borderColor="$borderColor"
                                    borderRadius={RADIUS.sm}
                                    backgroundColor="$backgroundStrong"
                                    focusWithinStyle={{ borderColor: '$primary' }}
                                >
                                    <Search
                                        size={13}
                                        color="$colorMuted"
                                    />
                                    <Input
                                        unstyled
                                        flex={1}
                                        height="100%"
                                        fontSize={13}
                                        color="$color"
                                        borderWidth={0}
                                        backgroundColor="transparent"
                                        outlineWidth={0}
                                        placeholder="Search all files…"
                                        placeholderTextColor="$colorFaint"
                                        value={query}
                                        onChangeText={setQuery}
                                    />
                                </XStack>
                                {!formDisabled && (
                                    <>
                                        <Button
                                            size="$2"
                                            height={32}
                                            backgroundColor="$backgroundStrong"
                                            borderWidth={1}
                                            borderColor="$borderColor"
                                            hoverStyle={{ backgroundColor: '$backgroundHover' }}
                                            icon={<AddRounded size={14} />}
                                            onPress={startNewFolder}
                                        >
                                            <SizableText size="$2">New folder</SizableText>
                                        </Button>
                                        <FileUploadButton
                                            theme="primary"
                                            size="$2"
                                            height={32}
                                            icon={<FileUploadRoundedIcon size={14} />}
                                            onChange={files => uploadFiles(files)}
                                        >
                                            <SizableText
                                                size="$2"
                                                color="white"
                                            >
                                                Upload
                                            </SizableText>
                                        </FileUploadButton>
                                    </>
                                )}
                            </XStack>
                        </XStack>

                        {/* Body */}
                        <YStack
                            flex={1}
                            overflow="auto"
                            className="hide-scrollbar"
                            paddingVertical="$3.5"
                            paddingHorizontal="$4"
                        >
                            {searchHits ? (
                                searchHits.length === 0 ? (
                                    <SizableText
                                        alignSelf="center"
                                        paddingVertical="$8"
                                        size="$2"
                                        color="$colorMuted"
                                    >
                                        No files match “{query}”
                                    </SizableText>
                                ) : (
                                    <>
                                        <SizableText
                                            {...sectionHeaderStyle}
                                            paddingHorizontal="$1"
                                            paddingBottom="$2"
                                        >
                                            {searchHits.length}{' '}
                                            {searchHits.length === 1 ? 'result' : 'results'} across all
                                            folders
                                        </SizableText>
                                        <YStack>
                                            {searchHits.map(({ file, location }) => (
                                                <FileRow
                                                    key={file.Key}
                                                    file={file}
                                                    location={location}
                                                    canDelete={canDelete}
                                                    opening={openingKey === file.Key}
                                                    {...rowCallbacks}
                                                />
                                            ))}
                                        </YStack>
                                    </>
                                )
                            ) : (
                                <>
                                    {(currentFolders.length > 0 || newFolder !== null) && (
                                        <>
                                            <SizableText
                                                {...sectionHeaderStyle}
                                                paddingHorizontal="$1"
                                                paddingBottom="$2"
                                            >
                                                Folders · {currentFolders.length}
                                            </SizableText>
                                            <View
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns:
                                                        'repeat(auto-fill, minmax(260px, 1fr))',
                                                    gap: 8,
                                                }}
                                            >
                                                {newFolder !== null && (
                                                    <XStack
                                                        alignItems="center"
                                                        gap="$3"
                                                        paddingVertical="$3"
                                                        paddingHorizontal="$3.5"
                                                        borderWidth={1}
                                                        borderColor="$primary"
                                                        borderRadius={RADIUS.md}
                                                        backgroundColor="$backgroundStrong"
                                                        opacity={creatingFolder ? 0.7 : 1}
                                                    >
                                                        <View
                                                            width={36}
                                                            height={36}
                                                            borderRadius={8}
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            backgroundColor="$accentSoft"
                                                        >
                                                            <Folder
                                                                size={20}
                                                                color="$accentText"
                                                            />
                                                        </View>
                                                        {creatingFolder ? (
                                                            <SizableText
                                                                flex={1}
                                                                minWidth={0}
                                                                size="$2"
                                                                fontWeight="500"
                                                                color="$color"
                                                                numberOfLines={1}
                                                            >
                                                                {newFolder}
                                                            </SizableText>
                                                        ) : (
                                                            <Input
                                                                unstyled
                                                                flex={1}
                                                                fontSize={13}
                                                                fontWeight="500"
                                                                color="$color"
                                                                borderWidth={0}
                                                                backgroundColor="transparent"
                                                                outlineWidth={0}
                                                                autoFocus
                                                                value={newFolder}
                                                                onChangeText={setNewFolder}
                                                                onKeyPress={(e: any) => {
                                                                    if (e.nativeEvent.key === 'Enter')
                                                                        commitNewFolder()
                                                                    if (e.nativeEvent.key === 'Escape')
                                                                        cancelNewFolder()
                                                                }}
                                                            />
                                                        )}
                                                        {creatingFolder ? (
                                                            <Spinner
                                                                size="small"
                                                                color="$primary"
                                                            />
                                                        ) : (
                                                            <XStack
                                                                gap="$1"
                                                                alignItems="center"
                                                                flexShrink={0}
                                                            >
                                                                {/* preventDefault on mousedown keeps the
                                                                    input focused so a failed (duplicate)
                                                                    confirm leaves it ready to edit. */}
                                                                <Button
                                                                    size="$2"
                                                                    circular
                                                                    chromeless
                                                                    width={28}
                                                                    height={28}
                                                                    padding={0}
                                                                    backgroundColor="transparent"
                                                                    hoverStyle={{
                                                                        backgroundColor: '$backgroundHover',
                                                                    }}
                                                                    icon={<CloseRounded size={15} />}
                                                                    onMouseDown={(e: any) =>
                                                                        e.preventDefault()
                                                                    }
                                                                    onPress={() => {
                                                                        cancelNewFolder()
                                                                    }}
                                                                />
                                                                <Button
                                                                    size="$2"
                                                                    circular
                                                                    theme="primary"
                                                                    width={28}
                                                                    height={28}
                                                                    padding={0}
                                                                    icon={
                                                                        <Check
                                                                            size={15}
                                                                            color="white"
                                                                        />
                                                                    }
                                                                    onMouseDown={(e: any) =>
                                                                        e.preventDefault()
                                                                    }
                                                                    onPress={() => {
                                                                        commitNewFolder()
                                                                    }}
                                                                />
                                                            </XStack>
                                                        )}
                                                    </XStack>
                                                )}
                                                {currentFolders.map(folder => (
                                                    <FolderCard
                                                        key={folder.Key}
                                                        folder={folder}
                                                        files={files}
                                                        onOpen={navigateTo}
                                                        dragDisabled={formDisabled}
                                                    />
                                                ))}
                                            </View>
                                        </>
                                    )}

                                    {(currentFiles.length > 0 || uploadingFiles.length > 0) && (
                                        <>
                                            <SizableText
                                                {...sectionHeaderStyle}
                                                paddingHorizontal="$1"
                                                paddingBottom="$2"
                                                marginTop={
                                                    currentFolders.length || newFolder !== null ? '$4' : 0
                                                }
                                            >
                                                Files · {currentFiles.length}
                                            </SizableText>
                                            <YStack>
                                                {uploadingFiles.map((file, i) => (
                                                    <FileSkeletonRow key={`uploading-${i}-${file.name}`} />
                                                ))}
                                                {currentFiles.map(file => (
                                                    <FileRow
                                                        key={file.Key}
                                                        file={file}
                                                        canDelete={canDelete}
                                                        opening={openingKey === file.Key}
                                                        {...rowCallbacks}
                                                    />
                                                ))}
                                            </YStack>
                                        </>
                                    )}

                                    {currentFolders.length === 0 &&
                                        currentFiles.length === 0 &&
                                        newFolder === null &&
                                        uploadingFiles.length === 0 && (
                                            <YStack
                                                alignItems="center"
                                                paddingVertical="$10"
                                                gap="$2"
                                            >
                                                <View
                                                    width={44}
                                                    height={44}
                                                    borderRadius={12}
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    backgroundColor="$backgroundRaised"
                                                >
                                                    <Folder
                                                        size={20}
                                                        color="$colorMuted"
                                                    />
                                                </View>
                                                <SizableText
                                                    size="$3"
                                                    fontWeight="500"
                                                    color="$color"
                                                >
                                                    This folder is empty
                                                </SizableText>
                                                <SizableText
                                                    size="$1"
                                                    color="$colorMuted"
                                                >
                                                    {formDisabled
                                                        ? 'No files have been added yet.'
                                                        : 'Upload a file or create a subfolder to get started.'}
                                                </SizableText>
                                            </YStack>
                                        )}
                                </>
                            )}
                        </YStack>
                    </YStack>
                </XStack>

                <DragOverlay>
                    {activeItem && (
                        <XStack
                            height={36}
                            paddingVertical="$2"
                            paddingHorizontal="$3"
                            alignItems="center"
                            gap="$2"
                            borderRadius={RADIUS.sm}
                            backgroundColor="$backgroundRaised"
                            borderWidth={1}
                            borderColor="$borderColorStrong"
                        >
                            {activeItem.isFolder ? (
                                <Folder size={16} />
                            ) : activeItem.isEmail ? (
                                <EmailRounded size={16} />
                            ) : (
                                <InsertDriveFileRounded size={16} />
                            )}
                            <SizableText
                                size="$2"
                                fontWeight="600"
                            >
                                {lastSegment(activeItem.name)}
                            </SizableText>
                        </XStack>
                    )}
                </DragOverlay>
            </DndContext>

            {/* Image / email preview */}
            <Dialog
                open={preview !== undefined && !preview.name.endsWith('.pdf')}
                onOpenChange={open => {
                    if (!open) setPreview(undefined)
                }}
            >
                <Dialog.Portal>
                    <Dialog.Overlay
                        key="overlay"
                        animation="quick"
                        opacity={0.5}
                        enterStyle={{ opacity: 0 }}
                        exitStyle={{ opacity: 0 }}
                    />
                    <Dialog.Content
                        key="content"
                        bordered
                        elevate
                        padding="$4"
                        borderRadius="$4"
                        width="90vw"
                        maxWidth={900}
                        gap="$4"
                    >
                        <XStack
                            alignItems="center"
                            justifyContent="space-between"
                            gap="$3"
                        >
                            <Dialog.Title
                                flex={1}
                                numberOfLines={1}
                                size="$4"
                            >
                                {preview?.name.replaceAll('%2F', '/')}
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <Button
                                    size="$2"
                                    circular
                                    chromeless
                                    width={30}
                                    height={30}
                                    padding={0}
                                    backgroundColor="transparent"
                                    hoverStyle={{ backgroundColor: '$backgroundHover' }}
                                    icon={<CloseRounded size={16} />}
                                    onPress={() => setPreview(undefined)}
                                />
                            </Dialog.Close>
                        </XStack>
                        <Dialog.Description
                            maxHeight="80vh"
                            overflow="auto"
                        >
                            {preview &&
                                !preview.name.endsWith('.pdf') &&
                                (preview.name.endsWith('.eml') || preview.name.endsWith('.msg') ? (
                                    <EmailModal
                                        name={preview.name}
                                        url={preview.url}
                                    />
                                ) : (
                                    <img
                                        src={preview.url}
                                        alt={preview.name}
                                        style={{
                                            display: 'block',
                                            margin: '0 auto',
                                            maxWidth: '100%',
                                            maxHeight: '76vh',
                                            objectFit: 'contain',
                                        }}
                                    />
                                ))}
                        </Dialog.Description>
                        <XStack justifyContent="flex-end">
                            <Dialog.Close asChild>
                                <Button
                                    variant="outlined"
                                    onPress={() => setPreview(undefined)}
                                >
                                    Close
                                </Button>
                            </Dialog.Close>
                        </XStack>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog>

            <PdfModal
                title={preview?.name ?? ''}
                pdf={preview?.name.endsWith('.pdf') ? preview.url : undefined}
                onClose={() => setPreview(undefined)}
                fileName={preview?.name ?? ''}
            />

            {/* Delete confirmation */}
            <Dialog
                open={deleteTarget !== undefined}
                onOpenChange={open => {
                    if (!open) setDeleteTarget(undefined)
                }}
            >
                <Dialog.Portal>
                    <Dialog.Overlay
                        key="overlay"
                        animation="quick"
                        opacity={0.5}
                        enterStyle={{ opacity: 0 }}
                        exitStyle={{ opacity: 0 }}
                    />
                    <Dialog.Content
                        key="content"
                        bordered
                        elevate
                        padding="$4"
                        borderRadius="$4"
                        gap="$4"
                    >
                        <Dialog.Title>Delete file</Dialog.Title>
                        <Dialog.Description>
                            <Text>
                                Are you sure you want to delete{' '}
                                {deleteTarget && lastSegment(deleteTarget.name)}?
                            </Text>
                        </Dialog.Description>
                        <XStack
                            gap="$2"
                            justifyContent="flex-end"
                        >
                            <Button
                                variant="outlined"
                                onPress={() => setDeleteTarget(undefined)}
                            >
                                Cancel
                            </Button>
                            <Button
                                theme="red"
                                icon={<DeleteRounded size={16} />}
                                onPress={confirmDelete}
                            >
                                Delete
                            </Button>
                        </XStack>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog>
        </YStack>
    )
}

export default Files
